
import {defineComponent} from "vue";

export default defineComponent({
    name: "DevicePrice",
    filters: {
        replacePointByComma(price: string) {
            return price.toString().replace('.', ',');
        },
        centsHandler(price: string) {
            const wholePart = price.split(',')[0];
            const cents = price.split(',')[1];
            if (typeof cents !== 'undefined') {
                // Si les centimes sont à '0' ou '00', on ne les affiche pas
                if (cents === '0' || cents === '00') {
                    return wholePart;
                    // Si seulement les dixièmes sont présents sans des centièmes, on ajoute un 0 (cas d'un float renvoyé par le back).
                } else if (cents.length === 1) {
                    return price.concat('0');
                } else {
                    return price;
                }
            } else {
                return price;
            }
        }
    },
    props: {
        // plusieurs prix selon le forfait
        multiplePrice: {
            type: Boolean,
            required: false,
            default: false
        },
        compactPrice: {
            type: Boolean,
            required: false,
            default: false
        },
        isMini: {
            type: Boolean,
            required: false,
            default: false
        },
        customPrice: {
            type: Boolean,
            required: false,
            default: false
        },
        // prix total avec promo
        totalPriceWithPromo: {
            type: Number,
            default: 0
        },
        // prix total sans promo (prix barré)
        totalPriceWithoutPromo: {
            type: Number,
            default: null
        },
        // montant de la mensualité
        monthlyAmountInCents: {
            type: Number,
            default: null
        },
        // durée du paiement mensuel
        monthlyDuration: {
            type: Number,
            default: null
        },
        // Libellé affiché quand le montant mensuel est non renseigné
        genericLabel: {
            type: String,
            default: null
        },
        // Libellé affiché quand le taux de prêt est à 0%
        loanRateZeroLabel: {
            type: String,
            required: false,
            default: null
        },
        isCentsFormat: {
            type: Boolean,
            default: false
        },
        isPriceInversionEnabled: {
            type: Boolean,
            required: true
        },
        advantageAccessibility: {
            type: String,
            required: false,
            default: null
        },
    },
    computed: {
        formattedTotalPriceWithPromo() {
            return this.formattedPrice(this.totalPriceWithPromo);
        },
        formattedTotalPriceWithoutPromo() {
            return this.formattedPrice(this.totalPriceWithoutPromo);
        },
        formattedPriceWithPromo() {
            return this.formattedTotalPriceWithPromo;
        },
        formattedPriceWithoutPromo() {
            return this.formattedTotalPriceWithoutPromo;
        },
        formattedMonthlyPrice() {
            return this.formattedPrice(this.monthlyAmountInCents);
        },
        // prix vocalisé lorsqu'on combine le prix avec d'autres informations
        // prix vocalisé couvrant tous les cas d'affichage
        priceAccessibility(): string {
            const promoPrice = this.vocalizePrice(this.totalPriceWithPromo);
            const withoutPromoPrice = this.vocalizePrice(this.totalPriceWithoutPromo);
            const monthlyPrice = this.vocalizePrice(this.monthlyAmountInCents);
            const parts: string[] = [];

            // "à partir de"
            const prefix = this.multiplePrice ? "à partir de " : "";

            // Mise en avant de la mensualité (inversion de prix)
            if (this.isPriceInversionEnabled) {
                parts.push(prefix + this.monthlyDuration + " mensualités de " + monthlyPrice + " euros");
                if (this.loanRateZeroAccessibility) {
                    parts.push(this.loanRateZeroAccessibility);
                }
                let cashPart = "ou " + promoPrice + " euros";
                if (withoutPromoPrice !== undefined) {
                    cashPart += " au lieu de " + withoutPromoPrice + " euros";
                }
                parts.push(cashPart);
                if (this.advantageAccessibility) {
                    parts.push(this.advantageAccessibility);
                }
                return parts.join(', ');
            }

            // Cas standard : prix comptant (+ prix barré si promo)
            let mainPart = prefix + promoPrice + " euros";
            if (withoutPromoPrice !== undefined) {
                mainPart += " au lieu de " + withoutPromoPrice + " euros";
            }
            parts.push(mainPart);

            // Mensualité ou libellé générique
            if (this.monthlyAmountInCents) {
                parts.push("ou " + this.monthlyDuration + " fois " + monthlyPrice + " euros");
            } else if (this.genericLabel) {
                parts.push(this.genericLabel.replace("*", " par carte bancaire dès 100 € d'achat mobile et accessoires"));
            }

            // Taux de prêt à 0 %
            if (this.loanRateZeroAccessibility) {
                parts.push(this.loanRateZeroAccessibility);
            }
            if (this.advantageAccessibility) {
                parts.push(this.advantageAccessibility);
            }
            return parts.join(', ');
        },
        paymentInfoLabel() {
            if (this.monthlyAmountInCents) {
                let monthlyPaymentAmount = this.formattedPrice(this.monthlyAmountInCents);
                let formattedMonthlyPaymentAmount = this.$options.filters.replacePointByComma(monthlyPaymentAmount);
                return "ou&nbsp;" + this.monthlyDuration + "&nbsp;x&nbsp;" + formattedMonthlyPaymentAmount + "&#8239;€";
            } else if (this.genericLabel) {
                return this.genericLabel;
            } else {
                return null;
            }
        },
        complementaryClass() {
            return {
                'ob1-price-compact': this.compactPrice,
                'ob1-price-mini': this.isMini
            }
        },
        loanRateZeroAccessibility() {
            if (!this.loanRateZeroLabel) {
                return null;
            }
            const base = "Taux zéro pourcent en financement sur mesure";
            return this.loanRateZeroLabel.includes("selon forfait")
                ? base + " selon le forfait"
                : base;
        }
    },
    methods: {
        // prix formaté en fonction de la prop "isCentsFormat"
        // - si false, pas de transformation, on a déjà un prix au format "XX,XX €"
        // - si true, la valeur passée à la prop "price" est en centimes, on divise donc par 100 pour avoir la valeur au format "XX,XX €"
        formattedPrice(price?: number) {
            if (!price) {
                return undefined
            }
            if (this.isCentsFormat) {
                return (price / 100).toString()
            } else {
                return price
            }
        },
        // prix vocalisé : virgule décimale + gestion des centimes (comme à l'affichage)
        vocalizePrice(price?: number): string | undefined {
            const formatted = this.formattedPrice(price);
            if (formatted === undefined) {
                return undefined;
            }
            const withComma = this.$options.filters.replacePointByComma(formatted);
            return this.$options.filters.centsHandler(withComma);
        }
    }
});
